import React, { useState } from 'react';
import {
  Box,
  CustomModalLayout,
  type CustomModalLayoutProps,
  Loader,
} from '@wix/design-system';
import { AppNameSection } from './components/AppNameSection';
import { NamespaceSection } from './components/NamespaceSection';
import type { NamespaceType } from '@wix/ambassador-devcenter-apps-v1-app/types';
import type { NamespacePrefixes } from '../useBlocksNamespaceModalState';
import { useTranslation } from '@wix/wix-i18n-config';

export const BlocksModalLayout = ({
  dataHook,
  closeModal,
  submit,
  isValidated,
  setIsValidated,
  name,
  setName,
  nameError,
  isNameValidating,
  namespace,
  setNamespace,
  namespacePrefixes,
  selectedNamespacePrefixType,
  setSelectedNamespacePrefixType,
  isNamespaceValidating,
  namespaceError,
}: BlocksModalLayoutProps) => {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  const defaultModalProps: Partial<CustomModalLayoutProps> = {
    height: 'auto',
    width: 600,
    dataHook,
    title: t('app.template.page.blocks.namespace.modal.header'),
    subtitle: t('app.template.page.blocks.namespace.modal.subtitle'),
    showHeaderDivider: true,
    secondaryButtonText: t(
      'app.template.page.blocks.namespace.modal.label.cancel',
    ),
    secondaryButtonOnClick: () => closeModal(),
    onCloseButtonClick: () => closeModal(),
  };

  if (name === undefined || namespacePrefixes === undefined) {
    return (
      <CustomModalLayout {...defaultModalProps} height={300}>
        <Box align="center" direction="vertical" height="100%" display="grid">
          <Loader />
        </Box>
      </CustomModalLayout>
    );
  }

  return (
    <CustomModalLayout
      {...defaultModalProps}
      showFooterDivider
      primaryButtonText={t(
        'app.template.page.blocks.namespace.modal.label.cta',
      )}
      primaryButtonOnClick={submit}
      primaryButtonProps={{ disabled: !isValidated }}
    >
      <Box direction="vertical">
        <AppNameSection
          isNameValidating={isNameValidating}
          nameError={nameError}
          namespaceError={namespaceError}
          name={name}
          setName={setName}
          setIsValidated={setIsValidated}
          showAdvanced={showAdvanced}
          setShowAdvanced={setShowAdvanced}
        />
        <NamespaceSection
          showAdvanced={showAdvanced}
          namespace={namespace}
          setNamespace={setNamespace}
          availablePrefixes={namespacePrefixes}
          setSelectedPrefixType={(prefix: NamespaceType) => {
            setIsValidated(false);
            setSelectedNamespacePrefixType(prefix);
            setNamespace(namespace);
          }}
          selectedPrefixType={selectedNamespacePrefixType}
          isValidating={isNamespaceValidating}
          error={namespaceError}
        />
      </Box>
    </CustomModalLayout>
  );
};

export type BlocksModalLayoutProps = {
  dataHook: string;
  closeModal: () => void;
  submit: () => void;
  isValidated: boolean;
  setIsValidated: (isValidated: boolean) => void;
  name: string;
  setName: (name: string) => void;
  nameError: string;
  isNameValidating: boolean;
  namespacePrefixes: NamespacePrefixes;
  namespace: string;
  setNamespace: (namespace: string) => void;
  selectedNamespacePrefixType: NamespaceType;
  setSelectedNamespacePrefixType: (prefix: NamespaceType) => void;
  isNamespaceValidating: boolean;
  namespaceError?: string;
};
